import './EditableTitle.scss';

import * as React from 'react';

import cx from 'classnames';
import TextareaAutosize from 'react-textarea-autosize';

import { useAutoFocus } from '~shared/components/helpers';
import { IconPencil } from '~shared/components/icons/large';

export interface Props {
  text: string;
  onChange?: (text: string) => void;
  placeholderText?: string;
  isDisabled?: boolean;
  maxLength?: number;
  /** Should the text automatically be selected when the field is focused? */
  autoSelect?: boolean;
  a11yLabel: string;
}

export default function EditableTitle({
  placeholderText,
  a11yLabel,
  text,
  onChange,
  isDisabled = false,
  maxLength = null,
  autoSelect = false,
}: Props) {
  const [titleText, setTitleText] = React.useState(text);
  const [isFocused, setIsFocused] = React.useState(false);

  const dupeClasses = cx(
    'pl-6 py-3 m-0 overflow-hidden',
    'font-display text-lg md:text-xl leading-14 font-700 break-words'
  );

  const labelStyle = cx({
    'flex-1 lg:flex-auto lg:flex-grow-0 min-w-1 lg:min-w-2xl relative': true,
    'cursor-default': isDisabled,
  });

  const inputClass = cx(
    dupeClasses,
    'w-full h-full pr-20 text-blue-800 outline-none border-0 rounded-4 resize-none transition duration-100 ease-linear',
    'editable-title__input'
  );

  const textClass = cx(
    dupeClasses,
    'pr-4 opacity-0 whitespace-pre-wrap pointer-events-none'
  );

  const iconClass = cx(
    'mt-4 mr-4 relative flex-shrink-0 transition duration-100 ease-linear pointer-events-none',
    {
      'text-cyan-600': !isFocused,
      'text-grey-200': isFocused,
      'opacity-66': isDisabled,
    }
  );

  const autoFocus = useAutoFocus(autoSelect);

  return (
    <label className={labelStyle}>
      <div className="absolute inset-0">
        <TextareaAutosize
          ref={autoFocus}
          className={inputClass}
          aria-label={a11yLabel}
          defaultValue={text}
          placeholder={placeholderText}
          disabled={isDisabled}
          onChange={(event) => {
            const {
              target: { value },
            } = event;

            setTitleText(value);
            onChange?.(value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              return;
            } else if (event.key === 'Escape') {
              event.currentTarget.blur();
            }
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          minRows={1}
          spellCheck={false}
          maxLength={maxLength}
        />
      </div>
      <div className="flex justify-between items-start" aria-hidden="true">
        <div className={textClass}>{titleText}</div>
        <div className={iconClass}>
          <IconPencil />
        </div>
      </div>
    </label>
  );
}
